<template>
  <div class="youtube-callback-container">
    <div class="callback-content">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <h2>Connecting your YouTube account...</h2>
        <p>Please wait while we complete the connection.</p>
      </div>
      <div v-else-if="error" class="error-state">
        <h2>Connection Failed</h2>
        <p>{{ errorMessage }}</p>
        <p class="close-message">You can close this window and try again.</p>
      </div>
      <div v-else class="success-state">
        <h2>Success!</h2>
        <p>Your YouTube account has been connected.</p>
        <p class="close-message">This window will close automatically...</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'YouTubeCallback',
  data() {
    return {
      loading: true,
      error: false,
      errorMessage: '',
    };
  },
  mounted() {
    this.handleCallback();
  },
  methods: {
    handleCallback() {
      try {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search);

        // Check for backend response (success or error)
        const status = urlParams.get('status');
        const message = urlParams.get('message');
        const channelId = urlParams.get('channel_id');
        const channelTitle = urlParams.get('channel_title');
        const channelCustomUrl = urlParams.get('channel_customUrl');

        // Also get OAuth params
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const error = urlParams.get('error');

        // Check for backend error response
        if (status === 'error') {
          this.handleError(message || 'YouTube connection failed. Please try again.');
          return;
        }

        // Check if URL contains PHP/backend error indicators
        if (window.location.href.includes('Undefined variable') ||
            window.location.href.includes('Fatal error') ||
            window.location.href.includes('Parse error')) {
          this.handleError('Backend error occurred. Please contact support or try again later.');
          return;
        }

        // Check for Google OAuth errors
        if (error) {
          this.handleError(`OAuth error: ${error}`);
          return;
        }

        // Check if backend processed successfully
        if (status === 'success' && channelId) {
          // Backend successfully processed the OAuth
          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'youtube-oauth-success',
                status: 'success',
                channel: {
                  id: channelId,
                  title: channelTitle || 'YouTube Channel',
                  customUrl: channelCustomUrl || ''
                },
                state: state
              },
              window.location.origin
            );

            this.loading = false;

            // Close the popup after a short delay
            setTimeout(() => {
              window.close();
            }, 1500);
          } else {
            this.handleError('Parent window not found. Please try again.');
          }
          return;
        }

        // Fallback: If we just have code and state (backend hasn't processed yet)
        if (code && state) {
          // Send message to parent window (opener)
          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'youtube-oauth-success',
                code: code,
                state: state,
              },
              window.location.origin
            );

            this.loading = false;

            // Close the popup after a short delay
            setTimeout(() => {
              window.close();
            }, 1500);
          } else {
            this.handleError('Parent window not found. Please try again.');
          }
          return;
        }

        // If we get here, something is wrong
        this.handleError('Missing required parameters');
      } catch (err) {
        console.error('Callback error:', err);
        this.handleError('An unexpected error occurred');
      }
    },

    handleError(message) {
      this.loading = false;
      this.error = true;
      this.errorMessage = message;

      // Send error message to parent if available
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'youtube-oauth-error',
            error: message,
          },
          window.location.origin
        );
      }

      // Auto-close after 5 seconds on error
      setTimeout(() => {
        window.close();
      }, 5000);
    },
  },
};
</script>

<style scoped>
.youtube-callback-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Poppins', sans-serif;
}

.callback-content {
  background: white;
  border-radius: 20px;
  padding: 3rem 2rem;
  max-width: 400px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.loading-state h2,
.error-state h2,
.success-state h2 {
  color: #333;
  margin-bottom: 1rem;
  font-size: 24px;
  font-weight: 600;
}

.loading-state p,
.error-state p,
.success-state p {
  color: #666;
  font-size: 16px;
  margin-bottom: 0.5rem;
}

.close-message {
  color: #999;
  font-size: 14px;
  margin-top: 1.5rem;
}

/* Loading Spinner */
.spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 1.5rem;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Success State */
.success-state h2 {
  color: #10b981;
}

/* Error State */
.error-state h2 {
  color: #ef4444;
}
</style>
